import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import { FeatureBadges } from '@/components/MicroTrustBadges';
import { FileText, CreditCard, Shield, Download, CheckCircle, AlertCircle, Building2, FileCheck, Banknote } from "lucide-react";

const LcPolicy = () => {
  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="LC Policy & Procedures | UCP600 Compliance | Specimen Documents | Padmora"
        description="Comprehensive Letter of Credit policy under ICC UCP600/ISP98 rules. Specimen documents, compliance framework, transferable LCs, back-to-back LCs, and anti-circumvention measures."
        image="/og-compliance.jpg"
        canonical="https://padmora.com/lc-policy"
        keywords={[
          "LC policy",
          "UCP600 compliance",
          "letter of credit procedures",
          "transferable LC",
          "back-to-back LC",
          "SBLC ISP98",
          "DLC documentary LC",
          "ICC rules",
          "trade finance compliance",
          "specimen documents",
          "LC checklist",
          "anti-circumvention",
          "document control",
          "assignment of proceeds"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated LC compliance checking",
            "AI document verification",
            "intelligent risk assessment",
            "compliance automation",
            "document authenticity validation"
          ],
          entities: [
            "UCP600",
            "ISP98",
            "Letter of Credit",
            "ICC Rules",
            "United States",
            "Trade Finance",
            "Compliance Framework"
          ],
          topics: [
            "Trade Finance Compliance",
            "Documentary Credit",
            "Legal Compliance",
            "Risk Management",
            "Financial Regulations"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "LC Policy & Procedures",
          "description": "Comprehensive Letter of Credit policy under ICC UCP600/ISP98 rules with specimen documents and compliance framework.",
          "mainEntity": {
            "@type": "CreativeWork",
            "name": "Padmora LC Policy",
            "description": "Official LC policy and procedures under international trade finance rules",
            "about": [
              {
                "@type": "Thing",
                "name": "UCP600",
                "description": "Uniform Customs and Practice for Documentary Credits"
              },
              {
                "@type": "Thing",
                "name": "ISP98",
                "description": "International Standby Practices"
              }
            ],
            "hasPart": [
              {
                "@type": "CreativeWork",
                "name": "LC Checklist",
                "description": "Comprehensive checklist for UCP600 compliance"
              },
              {
                "@type": "CreativeWork",
                "name": "Specimen Documents",
                "description": "Sample LC MT700, SGS inspection certificates, and insurance documents"
              }
            ]
          },
          "publisher": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          }
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "LC Policy", url: "https://padmora.com/lc-policy" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              LC Policy & Procedures
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              We operate exclusively under ICC rules (UCP600 for documentary LCs; ISP98/URDG for standby/guarantees)
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="/docs/Padmora__LC_Checklist_(UCP_600)__1-Page.pdf"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download our LC Checklist (UCP 600)
                </a>
              </Button>
            </motion.div>
          </motion.div>

                    <TradingTrustStrip />

          {/* Partners */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* LC Types */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            LC Types & Applications
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-2">
                <CreditCard className="h-6 w-6" />
                Transferable LC (Preferred)
              </h3>
              <p className="text-slate-600 mb-6">
                Single LC issued by buyer → transferred to seller; we substitute our invoice. Lowest friction and cost.
              </p>
              <FeatureBadges
                features={[
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Single LC structure" },
                  { icon: <Shield className="h-4 w-4" />, label: "Bank guarantee" },
                  { icon: <FileText className="h-4 w-4" />, label: "Invoice substitution" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "Cost efficient" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-2">
                <CreditCard className="h-6 w-6" />
                Back-to-Back LC
              </h3>
              <p className="text-slate-600 mb-6">
                Used when buyer's LC is non-transferable or requires term changes. Sub-LC issued by our bank against the master LC.
              </p>
              <FeatureBadges
                features={[
                  { icon: <Shield className="h-4 w-4" />, label: "Bank-to-bank security" },
                  { icon: <FileText className="h-4 w-4" />, label: "Term modifications" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "Non-transferable LCs" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Full compliance" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-2">
                <Shield className="h-6 w-6" />
                SBLC (ISP98)
              </h3>
              <p className="text-slate-600 mb-6">
                Accepted if seller ships on open/CAD backed by confirmed SBLC, or if our bank uses it as collateral to issue a sub-LC.
              </p>
              <FeatureBadges
                features={[
                  { icon: <Shield className="h-4 w-4" />, label: "Standby guarantee" },
                  { icon: <FileText className="h-4 w-4" />, label: "Open credit terms" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "Bank collateral" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Flexible terms" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900 flex items-center gap-2">
                <CreditCard className="h-6 w-6" />
                DLC (Documentary LC)
              </h3>
              <p className="text-slate-600 mb-6">
                Standard LC requiring specific shipping documents with bank confirmation and negotiation available.
              </p>
              <FeatureBadges
                features={[
                  { icon: <FileText className="h-4 w-4" />, label: "Document requirements" },
                  { icon: <Shield className="h-4 w-4" />, label: "Bank confirmation" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "Negotiation terms" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Standard compliance" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Document Set */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-8"
            >
              Required Document Set
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Standard documentation under UCP600 rules for clean presentation
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Commercial Documents
                </h3>
                <FeatureBadges
                  features={[
                    { icon: <FileText className="h-4 w-4" />, label: "B/L (clean, to order)" },
                    { icon: <FileText className="h-4 w-4" />, label: "Commercial invoice" },
                    { icon: <FileText className="h-4 w-4" />, label: "Packing list" },
                    { icon: <FileText className="h-4 w-4" />, label: "Certificate of Origin" }
                  ]}
                  align="left"
                  size="sm"
                  className="mt-4"
                />
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Insurance & Inspection
                </h3>
                <FeatureBadges
                  features={[
                    { icon: <Shield className="h-4 w-4" />, label: "Insurance ICC(A)" },
                    { icon: <CheckCircle className="h-4 w-4" />, label: "SGS inspection certificate" },
                    { icon: <FileText className="h-4 w-4" />, label: "Quality certificate" },
                    { icon: <FileText className="h-4 w-4" />, label: "Weight certificate" }
                  ]}
                  align="left"
                  size="sm"
                  className="mt-4"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Specimen Documents */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-8"
            >
              Specimen Document Set
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Illustrative documents with SPECIMEN watermark (not linked to actual transactions)
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">LC MT700</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Specimen LC with key clauses and conditions
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_LC_MT700_Template_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">SGS Scope</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Inspection certificate format and requirements
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_SGS_QQ_Scope_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Insurance ICC(A)</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Marine cargo insurance certificate format
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_Insurance_ICC_A_Certificate_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Term Sheet</h3>
                <p className="text-slate-600 text-sm mb-4">
                  FOB→CFR transaction terms and conditions
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_Term_Sheet_FOB_to_CFR_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* LC Checklist Download */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold mb-8"
            >
              LC Checklist
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Download our comprehensive 1-page LC checklist to ensure compliance with UCP600 requirements
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                className="px-8 py-4 bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a href="/docs/Padmora__LC_Checklist_(UCP_600)__1-Page.pdf" download target="_blank" rel="noopener noreferrer">
                  <Download className="h-5 w-5 mr-2" />
                  Download LC Checklist (PDF)
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Anti-circumvention */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold mb-8"
            >
              Anti-Circumvention Policy
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm text-center hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3 text-slate-900">Counterparty Disclosure</h3>
                <p className="text-slate-600 text-sm">Counterparties disclosed under NDA at LC-ready stage</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm text-center hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3 text-slate-900">Document Control</h3>
                <p className="text-slate-600 text-sm">We control document presentation and assignment of proceeds</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border/50 p-6 rounded-lg shadow-sm text-center hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3 text-slate-900">Proceeds Assignment</h3>
                <p className="text-slate-600 text-sm">Assignment of proceeds clauses in all LCs to prevent circumvention</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LcPolicy;
